/**
 * Typed Auth API call wrappers.
 * All functions use the shared Axios client which injects the Bearer token.
 */
import type { AxiosResponse } from 'axios';
import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RevokeTokenRequest,
  ForgotPasswordApiRequest,
  ForgotPasswordApiResponse,
  VerifyPasswordResetOtpApiRequest,
  VerifyPasswordResetOtpApiResponse,
  ValidatePasswordResetTokenResponse,
  ResetPasswordApiRequest,
  ChangePasswordApiRequest,
} from './authTypes';
import { postRefreshToken } from './tokenRefresh';


const authApi = {
  /**
   * POST /api/v1/Auth/Login
   * Returns JWT tokens on success.
   */
  login: (payload: LoginRequest): Promise<AxiosResponse<LoginResponse>> =>
    apiClient.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, payload),

  /**
   * POST /api/v1/Auth/LoginSSO
   * Authenticates a user with social auth token (Google, Apple, Facebook).
   */
  loginSSO: (payload: import('./authTypes').LoginSSORequest): Promise<AxiosResponse<LoginResponse>> =>
    apiClient.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN_SSO, payload),

  /**
   * POST /api/v1/Auth/RefreshToken
   * Exchanges a refresh token for a new access token.
   * Uses bare Axios via postRefreshToken to avoid apiClient 401 retry loops.
   */
  refreshToken: (
    payload: RefreshTokenRequest,
  ): Promise<AxiosResponse<RefreshTokenResponse>> =>
    postRefreshToken(payload),

  /**
   * POST /api/v1/Auth/RevokeToken
   * Invalidates the refresh token (server-side logout).
   * Returns 200 with an empty body on success.
   */
  revokeToken: (payload: RevokeTokenRequest): Promise<AxiosResponse<void>> =>
    apiClient.post<void>(API_ENDPOINTS.AUTH.REVOKE_TOKEN, payload),

  /**
   * POST /api/v1/Auth/ForgotPassword
   * Sends a password-reset link to the provided email address.
   */
  forgotPassword: (
    payload: ForgotPasswordApiRequest,
  ): Promise<AxiosResponse<ForgotPasswordApiResponse>> =>
    apiClient.post<ForgotPasswordApiResponse>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, payload),

  /**
   * POST /api/v1/Auth/VerifyPasswordResetOtp
   * Verifies OTP for a password reset token.
   * Answers 200 with `{ isValid: false }` when the code is wrong — check the
   * body, or call `authService.verifyPasswordResetOtp` which rejects for you.
   */
  verifyPasswordResetOtp: (
    payload: VerifyPasswordResetOtpApiRequest,
  ): Promise<AxiosResponse<VerifyPasswordResetOtpApiResponse>> =>
    apiClient.post<VerifyPasswordResetOtpApiResponse>(
      API_ENDPOINTS.AUTH.VERIFY_PASSWORD_RESET_OTP,
      payload,
    ),

  /**
   * GET /api/v1/Auth/ValidatePasswordResetToken?passwordResetToken=…
   * Validates a password-reset token from the emailed reset link.
   */
  validatePasswordResetToken: (
    passwordResetToken: string,
  ): Promise<AxiosResponse<ValidatePasswordResetTokenResponse>> =>
    apiClient.get<ValidatePasswordResetTokenResponse>(
      API_ENDPOINTS.AUTH.VALIDATE_PASSWORD_RESET_TOKEN,
      { params: { passwordResetToken } },
    ),

  /**
   * POST /api/v1/Auth/ResetPassword
   * Resets the user's password using the validated token.
   * Returns 200 with an empty body on success.
   */
  resetPassword: (
    payload: ResetPasswordApiRequest,
  ): Promise<AxiosResponse<void>> =>
    apiClient.post<void>(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload),

  changePassword: (
    payload: ChangePasswordApiRequest,
  ): Promise<AxiosResponse<void>> =>
    apiClient.post<void>(API_ENDPOINTS.ACCOUNT.CHANGE_PASSWORD, payload),
};

export default authApi;
